import React, { useEffect, useState } from 'react';
import { Label, ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import { FieldValidationErrors } from './FieldValidationErrors';
import { StringInputWithEventViewModel } from '../Types';
import {
  getFormFieldAriaProps,
  handleConditionalRequiredEvent,
  handleErrorEvent,
  handleOnChangeWithEvent,
  handleSetValueEvent,
} from '../Functions';
import { useDictionary } from 'lib/utils';

export const SingleLineTextWithEvents: React.FunctionComponent<ValueFieldProps<StringInputWithEventViewModel>> = (
  props
) => {
  const { field, onChange, errors, tracker, value } = props;
  const currentFieldName = field.model?.name || '';
  const onChangeEventMessage = field.model?.onChangeEventMessage || '';
  const inputFieldType = field.model?.inputFieldType || 'text';
  const [currentFieldValue, setCurrentFieldValue] = useState(value);
  const [customError, setCustomError] = useState<string>();
  const [isConditionalRequired, setIsConditionalRequired] = useState(false);
  const [isRequiredError, setIsRequiredError] = useState(false);

  const textAreaInputType = 'textarea';
  // Localized onChange event handler - it will be called both on onChange by user or external set value event
  const handleOnChange = (fieldValue: string, triggerEvent = true) => {
    setCurrentFieldValue(fieldValue);

    // We do not want to re-trigger the onChangeEventMessage event when the change is triggered by the event itself to avoid infinite loop
    if (triggerEvent) {
      console.log('Handle field state with onChangeEventMessage event: ', onChangeEventMessage, fieldValue);
    } else {
      console.log('Handle field state without onChangeEventMessage event: ', fieldValue);
    }

    // Only trigger if flag is set and event message is provided
    triggerEvent && handleOnChangeWithEvent(field, fieldValue, onChange);
  };

  const handleConditionalRequired = () => {
    const $hiddenField = document.getElementById(`hidden_${currentFieldName}_conditional_required`);
    let fieldIsRequired = false;

    if ($hiddenField) {
      const $hiddenFieldValue = $hiddenField.getAttribute('data-value') || '';
      fieldIsRequired = !!$hiddenFieldValue;
    }
    setIsConditionalRequired(fieldIsRequired);
    setIsRequiredError(!currentFieldValue && fieldIsRequired);
  };

  const handleError = (fieldValue: string) => {
    setCustomError(fieldValue);
  };

  const customErrorText = useDictionary(customError || '');
  const combinedErrors = [
    ...(props.errors ?? []),
    ...(customError ? [customErrorText] : []),
    ...(isRequiredError ? [`${field.model.title} is required`] : []),
  ];

  useEffect(() => {
    const handleFieldMessages = (postMessageEvent: MessageEvent) => {
      handleSetValueEvent(postMessageEvent, currentFieldName, handleOnChange);
      handleConditionalRequiredEvent(postMessageEvent, currentFieldName, handleConditionalRequired);
    };

    const errorHandler = (postMessageEvent: MessageEvent) => {
      handleErrorEvent(postMessageEvent, currentFieldName, handleError);
    };

    window.addEventListener('message', handleFieldMessages);
    window.addEventListener('message', errorHandler);

    return () => {
      window.removeEventListener('message', handleFieldMessages);
      window.removeEventListener('message', errorHandler);
    };
  });

  return (
    <>
      <Label {...props} />
      <input type="hidden" id={`hidden_${field.model.name}_conditional_required`} value="" />
      {inputFieldType === textAreaInputType ? (
        <textarea
          className={field.model.cssClass}
          id={field.valueField.id}
          name={field.valueField.name}
          maxLength={field.model.maxLength}
          rows={4}
          onChange={(e) => {
            setCustomError(undefined);
            isConditionalRequired && setIsRequiredError(!e.target.value);
            handleOnChangeWithEvent(field, e.target.value, onChange, handleOnChange);
          }}
          onFocus={() => tracker.onFocusField(field, currentFieldValue)}
          onBlur={() => tracker.onBlurField(field, currentFieldValue, errors)}
          {...getFormFieldAriaProps(field, combinedErrors)}
        >
          {currentFieldValue}
        </textarea>
      ) : (
        <input
          type={inputFieldType}
          className={field.model.cssClass}
          id={field.valueField.id}
          name={field.valueField.name}
          value={currentFieldValue}
          maxLength={field.model.maxLength}
          placeholder={field.model.placeholderText}
          onChange={(e) => {
            setCustomError(undefined);
            isConditionalRequired && setIsRequiredError(!e.target.value);
            handleOnChangeWithEvent(field, e.target.value, onChange, handleOnChange);
          }}
          onFocus={() => tracker.onFocusField(field, currentFieldValue)}
          onBlur={() => tracker.onBlurField(field, currentFieldValue, errors)}
          {...getFormFieldAriaProps(field, combinedErrors)}
        />
      )}
      <FieldValidationErrors {...props} errors={combinedErrors} />
    </>
  );
};

export default SingleLineTextWithEvents;
